<script lang="ts">
  import PubSub from 'pubsub-js'
  import { lang } from '@/store'
  import { i18n } from '@/plugins/util'
  import PageWalletTransactions from '@/components/appDriver/wallet/PageWalletTransactions.svelte'
  import WalletAmount from '@/components/appDriver/wallet/WalletAmount.svelte'
  import type { WalletSummary } from '@/components/appDriver/wallet/types'
  import {formatLakAmount, getWalletActionState, maskBankAccountNumber} from '@/components/appDriver/wallet/utils'

  export let summary: WalletSummary
  export let isActionPending = false
  export let onPayNow: () => void | Promise<void> = () => {}
  export let onWithdraw: () => void | Promise<void> = () => {}

  $: actionState = getWalletActionState(summary.balance)
  $: isNearSoftLimit = summary.balance <= summary.softLimit && summary.balance > summary.hardLimit
  $: isHardLimitReached = summary.balance <= summary.hardLimit
  $: hasBankDetails = summary.isBankDetailsEnable && (summary.accountNumber || summary.accountHolderName)
  $: canWithdraw = summary.balance > 0

  function openTransactions() {
    PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageWalletTransactions })
  }

  function openPayNow() {
    void onPayNow()
  }

  function openWithdraw() {
    if (!canWithdraw || isActionPending) {
      return
    }

    void onWithdraw()
  }
</script>

<section class="rounded-lg bg-white p-4 shadow">
  <div class="text-center">
    <div class="text-sm font-semibold uppercase tracking-wide text-gray-400">
      {i18n($lang, ['Current Wallet Balance', 'ຍອດເງິນໃນບັນຊີປັດຈຸບັນ'])}
    </div>

    <div class="{summary.balance < 0 ? 'text-red-500' : 'text-blue-dark'} mt-3 text-4xl font-bold leading-none">
      <WalletAmount amount={summary.balance} symbol={summary.currencySymbol}/>
    </div>

    <div class="mx-auto mt-4 h-px max-w-[280px] bg-gray-300"></div>

    <div class="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-400">
      <div class="rounded bg-gray-50 px-2 py-2">
        {i18n($lang, ['Soft Limit:', 'ວົງເງິນຕິດລົບຂັ້ນຕໍ່າ:'])}
        <div class="mt-0.5 font-semibold text-amber-600">{formatLakAmount(summary.softLimit, { symbol: summary.currencySymbol })}</div>
      </div>
      <div class="rounded bg-gray-50 px-2 py-2">
        {i18n($lang, ['Hard Limit:', 'ວົງເງິນຕິດລົບສູງສຸດ:'])}
        <div class="mt-0.5 font-semibold text-red-500">{formatLakAmount(summary.hardLimit, { symbol: summary.currencySymbol })}</div>
      </div>
    </div>

    <div class="mt-5">
      <button
        type="button"
        class="rounded-lg bg-blue-primary px-5 py-2.5 text-sm font-bold uppercase text-white shadow-sm"
        on:click={openTransactions}
      >
        {i18n($lang, ['Recent Transactions', 'ປະຫວັດລາຍການຫຼ້າສຸດ'])}
      </button>
    </div>

    <div class="mt-5 rounded-lg bg-gray-50 px-4 py-3 text-left">
      {#if actionState.status === 'payable'}
        <div class="text-sm font-semibold text-red-500">{i18n($lang, ['Amount to Pay', 'ຈຳນວນທີ່ຕ້ອງຊຳລະ'])}</div>
        <div class="mt-1 text-2xl font-bold text-gray-800"><WalletAmount amount={actionState.payableAmount} symbol={summary.currencySymbol}/></div>
      {:else if actionState.status === 'withdrawable'}
        <div class="text-sm font-semibold text-green-600">{i18n($lang, ['Available to Withdraw', 'ຈຳນວນທີ່ຖອນໄດ້'])}</div>
        <div class="mt-1 text-2xl font-bold text-gray-800"><WalletAmount amount={actionState.withdrawableAmount} symbol={summary.currencySymbol}/></div>
      {:else}
        <div class="text-sm font-semibold text-gray-600">{i18n($lang, ['No outstanding balance', 'ບໍ່ມີຍອດຄ້າງ'])}</div>
        <div class="mt-1 text-sm text-gray-500">{i18n($lang, ['No payment or withdrawal is needed right now.', 'ຕອນນີ້ບໍ່ຈຳເປັນຕ້ອງຊຳລະ ຫຼື ຖອນເງິນ'])}</div>
      {/if}
    </div>

    {#if isHardLimitReached}
      <div class="mt-3 rounded-md bg-red-50 px-4 py-3 text-left text-sm text-red-600">
        {i18n($lang, ['Cash limit reached. Please pay now to continue receiving cash bookings.', 'ຮອດຂີດຈຳກັດເງິນສົດແລ້ວ ກະລຸນາຊຳລະເພື່ອຮັບວຽກເງິນສົດຕໍ່'])}
      </div>
    {:else if isNearSoftLimit}
      <div class="mt-3 rounded-md bg-amber-50 px-4 py-3 text-left text-sm text-amber-700">
        {i18n($lang, ['You are near the cash limit. Please pay soon to avoid cash booking restrictions.', 'ທ່ານໃກ້ຮອດຂີດຈຳກັດເງິນສົດ ກະລຸນາຊຳລະໄວໆນີ້'])}
      </div>
    {/if}

    {#if hasBankDetails}
      <div class="mt-4 rounded-lg bg-blue-50 px-4 py-3 text-left">
        <div class="text-xs font-semibold uppercase text-blue-primary">
          {i18n($lang, ['Bank Details', 'ຂໍ້ມູນບັນຊີທະນາຄານ'])}
        </div>
        {#if summary.accountHolderName}
          <div class="mt-2 truncate text-sm font-semibold text-blue-dark">{summary.accountHolderName}</div>
        {/if}
        {#if summary.accountNumber}
          <div class="mt-1 text-sm text-gray-600">{maskBankAccountNumber(summary.accountNumber)}</div>
        {/if}
      </div>
    {/if}
  </div>

  <div class="mt-4 grid grid-cols-2 gap-3 border-t border-gray-100 pt-4">
    <button
      type="button"
      class="rounded-lg px-3 py-3 text-base font-bold {canWithdraw ? 'bg-blue-dark text-white' : 'bg-gray-200 text-gray-400'}"
      disabled={!canWithdraw || isActionPending}
      on:click={openWithdraw}
    >
      {i18n($lang, ['Withdraw Now', 'ຖອນເງິນ'])}
    </button>

    <button
      type="button"
      class="rounded-lg px-3 py-3 text-base font-bold {isActionPending ? 'bg-gray-200 text-gray-400' : 'bg-blue-primary text-white'}"
      disabled={isActionPending}
      on:click={openPayNow}
    >
      {i18n($lang, ['Pay Now', 'ເຕີມເງິນ'])}
    </button>
  </div>
</section>
